import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect, ConnectedProps } from 'react-redux';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { ProductType } from '@tink/core/src/types';
import { stopPollingCredentials } from '../../../connectivity/services/credentials';
import {
  BankIdMachineView as BankIdMachineViewImport,
  BankIdMachineViewProps,
} from '../../../connectivity/screens/bank-id/BankIdMachineView';
import {
  AwaitingSupplementalInformation as AwaitingSupplementalInformationImport,
  AwaitingSupplementalInformationOwnProps,
} from '../../../income-check-v2/components/AwaitingSupplementalInformation';
import { ProviderEntity } from '../../../models/provider';
import {
  LoadingOverlay as LoadingOverlayImport,
  LoadingOverlayProps,
} from '../../../core/screens/LoadingOverlay/LoadingOverlay';
import { AppState } from '../../../stores/redux-store';
import { AwaitingScenario, Credentials, CredentialsEntity } from '../../../models/credentials';
import {
  PayAwaitingThirdpartyAuthenticationContainer as PayAwaitingThirdpartyAuthenticationContainerImport,
  PayAwaitingThirdpartyAuthenticationContainerOwnProps,
} from '../../../payments/legacy/components/PayAwaitingThirdpartyAuthenticationContainer/PayAwaitingThirdpartyAuthenticationContainer';
import {
  resetCredentials as resetCredentialsConnect,
  setCredentials as setCredentialsConnect,
} from '../../../stores/credentials-store';
import { setRedirectedCredentialsId as setRedirectedCredentialsIdConnect } from '../../../stores/app-state-store';
import { useCredentialsPoller } from '../../hooks/useCredentialsPoller/useCredentialsPoller';
import { useProduct } from '../../ApplicationProviders/ProductProvider/ProductProvider';
import { useConfiguration } from '../../ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { BackButton } from '../../organisms/Header/HeaderMolecule';
import { messages } from './messages';

export type AwaitingCredentialsPayOwnProps = {
  userId: string;
  provider: ProviderEntity;
  awaitingScenario: AwaitingScenario;
  footerDisclaimer: React.ReactNode | undefined;
  onExecute: VoidFunction | undefined;
  children?: React.ReactNode;
  autoSubmitCredentials?: boolean;
  isPaymentSca: boolean;
  LoadingOverlay?: React.ComponentType<LoadingOverlayProps>;
  AwaitingSupplementalInformation?: React.ComponentType<AwaitingSupplementalInformationOwnProps>;
  PayAwaitingThirdpartyAuthenticationContainer?: React.ComponentType<PayAwaitingThirdpartyAuthenticationContainerOwnProps>;
  BankIdMachineView?: React.ComponentType<BankIdMachineViewProps>;
} & BackButton;

export type AwaitingCredentialsPayProps = AwaitingCredentialsPayOwnProps & PropsFromRedux;

export const AwaitingCredentialsPayPresenter: React.FC<AwaitingCredentialsPayProps> = ({
  children,
  credentials,
  credentialsLoading,
  awaitingScenario,
  footerDisclaimer,
  onExecute,
  session,
  provider,
  autoSubmitCredentials = false,
  setCredentials,
  resetCredentials,
  setRedirectedCredentialsId,
  isPaymentSca,
  onBack,
  LoadingOverlay = LoadingOverlayImport,
  AwaitingSupplementalInformation = AwaitingSupplementalInformationImport,
  PayAwaitingThirdpartyAuthenticationContainer = PayAwaitingThirdpartyAuthenticationContainerImport,
  BankIdMachineView = BankIdMachineViewImport,
}) => {
  const { productType } = useProduct();
  const { data: configurationData } = useConfiguration();
  const { signCredentials } = useCredentialsPoller();

  if (credentialsLoading) {
    return (
      <>
        <LoadingOverlay />
        {!autoSubmitCredentials && children}
      </>
    );
  }

  // FIX: We need to render `overlay` as `undefined` here, because if we do not do that, `ProviderInputs` component is unmounted and username/password input values are cleared.
  if (!credentials || Credentials.hasError(credentials)) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  if (Credentials.isAwaitingSupplementalInformation(credentials)) {
    return <AwaitingSupplementalInformation credentials={credentials} provider={provider} />;
  }

  if (Credentials.isAwaitingThirdPartyAppAuthentication(credentials)) {
    return (
      <PayAwaitingThirdpartyAuthenticationContainer
        provider={provider}
        footer={footerDisclaimer}
        credentials={credentials}
        onBack={onBack}
        awaitingScenario={awaitingScenario}
      />
    );
  }

  if (Credentials.isAuthenticating(credentials) || Credentials.isCreated(credentials)) {
    return <LoadingOverlay />;
  }

  if (Credentials.isAwaitingMobileBankId(credentials)) {
    stopPollingCredentials();
    const onDone = (c: CredentialsEntity) => {
      setCredentials(c);

      signCredentials(c.id);
    };
    const onOpenBankId = (credentialsId: string) => {
      const isUsingTinkFlowFinishedScreen =
        userAgent.isAppleDevice && productType !== ProductType.PAY;

      if (isUsingTinkFlowFinishedScreen) setRedirectedCredentialsId(credentialsId);
    };

    return (
      <BankIdMachineView
        isPaymentSca={awaitingScenario === AwaitingScenario.PAYMENT}
        footerDisclaimer={footerDisclaimer}
        onExecute={onExecute}
        credentials={credentials}
        provider={provider}
        productType={productType}
        configurationData={configurationData}
        onDone={onDone}
        onError={setCredentials}
        onBack={resetCredentials}
        onOpenBankId={onOpenBankId}
      />
    );
  }

  if (Credentials.isUpdating(credentials)) {
    if (awaitingScenario === AwaitingScenario.PAYMENT || session?.sourceAccountNumber) {
      return <LoadingOverlay />;
    }

    return <LoadingOverlay text={<FormattedMessage {...messages.gettingAccountsInfo} />} />;
  }

  if (isPaymentSca) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  return <LoadingOverlay />;
};

const mapStateToProps = (state: AppState) => {
  return {
    userId: state.user.user?.id ?? '',
    credentials: state.credentials.data,
    credentialsLoading: state.credentials.loading,
    clientName: state.oauth.description?.clientName,
    session: state.session.data,
  };
};

const mapDispatchToProps = {
  setCredentials: setCredentialsConnect,
  resetCredentials: resetCredentialsConnect,
  setRedirectedCredentialsId: setRedirectedCredentialsIdConnect,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const AwaitingCredentialsPay = connector(AwaitingCredentialsPayPresenter);
